/**
 * Text Parser Utility
 *
 * Parses user-provided text input into structured song objects.
 * Supports various common formats for listing songs.
 */

/**
 * Parse user input text into an array of song objects
 *
 * Supports multiple formats:
 * - "Song Title - Artist"
 * - "Song Title by Artist"
 * - "Artist - Song Title" (detected via heuristics)
 * - "Song Title, Artist"
 * - Numbered lists: "1. Song Title - Artist"
 * - Bullet points: "- Song Title - Artist"
 *
 * @param {string} text - The raw input text
 * @returns {Array<ParsedSong>} Array of parsed song objects
 *
 * @typedef {Object} ParsedSong
 * @property {string} title - The parsed song title
 * @property {string} artist - The parsed artist name
 * @property {string} originalLine - The original input line
 * @property {number} lineNumber - The line number (1-indexed)
 * @property {'high'|'medium'|'low'} confidence - Parsing confidence
 */
export function parseInputText(text) {
  if (!text || typeof text !== 'string') {
    return [];
  }

  const lines = text
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0);

  return lines.map((line, index) => {
    const parsed = parseLine(line);
    return {
      ...parsed,
      originalLine: line,
      lineNumber: index + 1,
    };
  });
}

/**
 * Parse a single line into a song object
 * @param {string} line - A single line of input
 * @returns {Object} Parsed song with title, artist, and confidence
 */
function parseLine(line) {
  // Step 1: Clean up common list prefixes
  const cleaned = cleanLinePrefix(line);

  // Step 2: Try to extract title and artist using various separators
  const separators = [
    { pattern: /\s+[-–—]\s+/, type: 'dash', confidence: 'high' },
    { pattern: /\s+by\s+/i, type: 'by', confidence: 'high' },
    { pattern: /\s*[|]\s*/, type: 'pipe', confidence: 'medium' },
    { pattern: /\s*[/]\s*/, type: 'slash', confidence: 'medium' },
    { pattern: /\s*,\s*(?=[^,]+$)/, type: 'comma', confidence: 'medium' }, // Last comma only
  ];

  for (const { pattern, type, confidence } of separators) {
    const parts = cleaned.split(pattern);

    if (parts.length >= 2 && parts[0].trim() && parts[1].trim()) {
      const first = parts[0].trim();
      const second = parts.slice(1).join(' ').trim();

      // For "by" separator, second part is the artist
      if (type === 'by') {
        return {
          title: first,
          artist: second,
          confidence,
        };
      }

      // For dash separator, try to detect order
      // Usually "Title - Artist" but could be "Artist - Title"
      const { title, artist } = detectTitleArtistOrder(first, second);

      return {
        title,
        artist,
        confidence,
      };
    }
  }

  // No clear separator found - treat entire line as search query
  return {
    title: cleaned,
    artist: '',
    confidence: 'low',
  };
}

/**
 * Remove common list prefixes from a line
 * @param {string} line - The input line
 * @returns {string} Cleaned line without prefix
 */
function cleanLinePrefix(line) {
  return line
    // Remove numbered prefixes: "1.", "1)", "1-", "1:"
    .replace(/^\d+[\.\)\-:\s]+/, '')
    // Remove bullet points: "- ", "* ", "• ", "→ "
    .replace(/^[\-\*\•\→]\s*/, '')
    // Remove checkbox-style: "[ ] ", "[x] "
    .replace(/^\[.\]\s*/, '')
    // Clean up any remaining leading/trailing whitespace
    .trim();
}

/**
 * Try to detect whether the format is "Title - Artist" or "Artist - Title"
 *
 * Heuristics used:
 * - Shorter strings are more likely to be artist names
 * - Strings with common artist indicators (feat., ft., &) are artists
 * - Strings in quotes are likely titles
 *
 * @param {string} first - First part of the split
 * @param {string} second - Second part of the split
 * @returns {Object} Object with title and artist properties
 */
function detectTitleArtistOrder(first, second) {
  // Check for quoted title
  if (first.startsWith('"') || first.startsWith("'")) {
    return { title: first.replace(/["']/g, ''), artist: second };
  }
  if (second.startsWith('"') || second.startsWith("'")) {
    return { title: second.replace(/["']/g, ''), artist: first };
  }

  // Check for collaboration indicators (more likely in artist field)
  const collabPattern = /\b(feat\.?|ft\.?|featuring|with|&|and|vs\.?)\b/i;
  if (collabPattern.test(second) && !collabPattern.test(first)) {
    return { title: first, artist: second };
  }
  if (collabPattern.test(first) && !collabPattern.test(second)) {
    return { title: second, artist: first };
  }

  // Check for common title patterns (parentheses for remix, version, etc.)
  const titlePattern = /\((remix|remaster|version|edit|live|acoustic|radio|extended|original)\)/i;
  if (titlePattern.test(first)) {
    return { title: first, artist: second };
  }
  if (titlePattern.test(second)) {
    return { title: second, artist: first };
  }

  // Default: assume "Title - Artist" format (most common)
  return { title: first, artist: second };
}

/**
 * Build an optimized search query from a parsed song
 *
 * Combines title and artist for better search results,
 * and removes problematic characters.
 *
 * @param {ParsedSong} song - The parsed song object
 * @returns {string} Optimized search query
 */
export function buildSearchQuery(song) {
  const parts = [];

  if (song.title) {
    // Clean title: remove parenthetical info that might confuse search
    let cleanTitle = song.title
      .replace(/\s*\([^)]*remix[^)]*\)/gi, '') // Remove remix info
      .replace(/\s*\[[^\]]*\]/g, '')            // Remove bracket info
      .trim();

    // Keep original if cleaning removed everything
    parts.push(cleanTitle || song.title);
  }

  if (song.artist) {
    // Clean artist: simplify collaborations
    let cleanArtist = song.artist
      .replace(/\s*(feat\.?|ft\.?|featuring)\s+/gi, ' ') // Simplify feat
      .replace(/\s+&\s+/g, ' ')                          // Simplify &
      .trim();

    parts.push(cleanArtist || song.artist);
  }

  return parts.join(' ').trim();
}

/**
 * Validate parsed songs and identify potential issues
 *
 * @param {ParsedSong[]} songs - Array of parsed songs
 * @returns {Object} Validation result with stats and warnings
 */
export function validateParsedSongs(songs) {
  const stats = {
    total: songs.length,
    highConfidence: 0,
    mediumConfidence: 0,
    lowConfidence: 0,
    warnings: [],
  };

  songs.forEach((song, index) => {
    switch (song.confidence) {
      case 'high':
        stats.highConfidence++;
        break;
      case 'medium':
        stats.mediumConfidence++;
        break;
      case 'low':
        stats.lowConfidence++;
        stats.warnings.push({
          lineNumber: song.lineNumber,
          message: `Line ${song.lineNumber}: Could not parse title/artist separately`,
          originalLine: song.originalLine,
        });
        break;
    }

    // Check for very short entries
    if (song.title.length < 2) {
      stats.warnings.push({
        lineNumber: song.lineNumber,
        message: `Line ${song.lineNumber}: Title seems too short`,
        originalLine: song.originalLine,
      });
    }
  });

  return stats;
}

/**
 * Parse CSV content into song objects
 *
 * Expected columns: title (or song), artist
 * Optional columns: album
 *
 * @param {string} csvContent - Raw CSV content
 * @returns {ParsedSong[]} Array of parsed songs
 */
export function parseCSV(csvContent) {
  const lines = csvContent.split('\n').filter(line => line.trim());

  if (lines.length < 2) {
    throw new Error('CSV must have a header row and at least one data row');
  }

  // Parse header
  const header = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());

  // Find column indices
  const titleIndex = header.findIndex(h =>
    h === 'title' || h === 'song' || h === 'track' || h === 'name'
  );
  const artistIndex = header.findIndex(h =>
    h === 'artist' || h === 'artists' || h === 'performer'
  );

  if (titleIndex === -1) {
    throw new Error('CSV must have a "title", "song", "track", or "name" column');
  }

  // Parse data rows
  return lines.slice(1)
    .map((line, index) => {
      const cols = parseCSVLine(line);
      const title = cols[titleIndex]?.trim() || '';
      const artist = artistIndex >= 0 ? cols[artistIndex]?.trim() || '' : '';

      return {
        title,
        artist,
        originalLine: line,
        lineNumber: index + 2, // +2 because we skip header and use 1-indexed
        confidence: artist ? 'high' : 'medium',
      };
    })
    .filter(song => song.title);
}

/**
 * Parse a single CSV line, handling quoted fields
 *
 * @param {string} line - A CSV line
 * @returns {string[]} Array of field values
 */
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++;
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
}
